import { useContext, useEffect, useMemo, useState } from 'react';

import {
  Cancel,
  CheckCircle,
  Circle,
  Error,
  KeyboardArrowDown,
  Warning
} from '@mui/icons-material';
import {
  Stack,
  Typography,
  Divider,
  ButtonGroup,
  Button as MuiButton,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  AlertProps
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import Alert from '@/components/alert';
import Button from '@/components/button';
import Card from '@/components/card';
import Checkbox from '@/components/checkbox';
import Dialog from '@/components/dialog';
import NotRelevantIcon from '@/components/icons/compliance/NotRelevantIcon';
import OrganicIcon from '@/components/icons/compliance/OrganicIcon';
import { PostDetailsContext } from '@/contexts/PostDetailsContext';
import {
  useCreateCompliance,
  useDeleteCompliance,
  useUpdateCompliance
} from '@/hooks/queries/posts';
import useAlert from '@/hooks/useAlert';
import { Posts } from '@/models/posts';
import { formatDate } from '@/utils/datesAndTime/datesAndTime';

const alertReturn = (
  rating: string,
  isRelevant: boolean | null,
  adType: Posts.adType
) => {
  let capitalizedAdType = adType
    ? adType.charAt(0).toUpperCase() + adType.slice(1)
    : '';

  capitalizedAdType =
    capitalizedAdType === 'Questionable'
      ? 'Possible Advertisement'
      : capitalizedAdType;

  if (adType === 'organic') {
    return {
      title: 'Organic',
      content: '',
      icon: <OrganicIcon fontSize="large" color="secondary" />
    };
  }

  if (isRelevant === false) {
    return {
      title: 'Not Relevant',
      content: '',
      icon: <NotRelevantIcon fontSize="large" color="primary" />
    };
  }

  const alerts: Record<string, AlertProps> = {
    questionable: {
      title: 'Questionable',
      content: capitalizedAdType,
      icon: <Warning color="warning" fontSize="large" />
    },
    violation: {
      title: 'Violation',
      content: capitalizedAdType,
      icon: <Error color="error" fontSize="large" />
    },
    compliant: {
      title: 'Compliant',
      content: capitalizedAdType,
      icon: <CheckCircle color="success" fontSize="large" />
    },
    default: {
      title: 'Default',
      content: capitalizedAdType,
      icon: <Cancel />
    }
  };

  return alerts[rating] || alerts.default;
};

const selectColor: Record<
  Posts.ComplianceType,
  'success' | 'warning' | 'error' | undefined
> = {
  compliant: 'success',
  questionable: 'warning',
  violation: 'error',
  unknown: undefined,
  '': undefined
};

const PostCompliance = () => {
  const { postId, evaluationId, data, usedParams } =
    useContext(PostDetailsContext);
  const queryClient = useQueryClient();
  const alert = useAlert();

  const [compliance, setCompliance] = useState<Posts.ComplianceType>('');
  const [reason, setReason] = useState('');
  const [isEditing, setEditing] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isRelevant, setIsRelevant] = useState<boolean | null>(null);
  const [adType, setAdType] = useState<Posts.adType>('');
  const [isRated, setIsRated] = useState(false);
  const [author, setAuthor] = useState<{ name: string; date: string }>({
    name: '',
    date: ''
  });

  const updateQueryData = (newData: Posts.Compliance) => {
    queryClient.setQueryData(
      ['post', postId, usedParams],
      (oldData: Posts.Details | undefined) => ({
        ...oldData,
        evaluation: {
          ...oldData?.evaluation,
          compliance: {
            ...oldData?.evaluation.compliance,
            isRated: true,
            reason: newData.reason,
            isRelevant: newData.isRelevant,
            compliance: newData.compliance,
            adType: newData.adType,
            author: newData.author
          }
        }
      })
    );
    queryClient.invalidateQueries({
      queryKey: ['posts'],
      refetchType: 'all'
    });
  };

  const { mutate: createCompliance, isPending: isCreating } =
    useCreateCompliance(
      (newData: Posts.Compliance) => {
        updateQueryData(newData);
        alert.success('The rating was successfully created');
      },
      () => alert.error('Some error occurred while rating the compliance')
    );

  const { mutate: updateCompliance, isPending: isUpdating } =
    useUpdateCompliance(
      (newData: Posts.Compliance) => {
        updateQueryData(newData);
        setEditing(false);
        alert.success('The rating was successfully updated');
        queryClient.refetchQueries({ queryKey: ['violation'] });
      },
      () => alert.error('Some error occurred while rating the compliance')
    );

  const { mutate: deleteCompliance, isPending: isDeleting } =
    useDeleteCompliance(
      () => {
        queryClient.setQueryData(
          ['post', postId, usedParams],
          (oldData: Posts.Details | undefined) => ({
            ...oldData,
            evaluation: {
              ...oldData?.evaluation,
              compliance: {
                ...oldData?.evaluation.compliance,
                isRated: false,
                reason: '',
                isRelevant: null,
                compliance: 'unknown',
                adType: null,
                author: null
              }
            }
          })
        );
        alert.success('The rating was successfully deleted');
        queryClient.invalidateQueries({
          queryKey: ['posts'],
          refetchType: 'all'
        });
      },
      () => {
        alert.error('Some error occurred while deleting the rating');
      },
      () => {
        setDialogOpen(false);
      }
    );

  useEffect(() => {
    if (data?.evaluation?.compliance) {
      fillStates(data);
    }
  }, [data]);

  const fillStates = (data: Posts.Details) => {
    const {
      author: evaluationAuthor,
      isRelevant: evaluationIsRelevant = null,
      reason: evaluationReason,
      compliance: evaluationCompliance,
      adType: evaluationAdType,
      isRated: evaluationIsRated
    } = data.evaluation.compliance;
    setIsRated(evaluationIsRated);
    setIsRelevant(evaluationIsRelevant);
    setReason(evaluationReason);
    setCompliance(
      evaluationCompliance === 'unknown' ? '' : evaluationCompliance
    );
    setAdType(evaluationAdType);
    setAuthor(evaluationAuthor);
  };

  const handleClickSave = () => {
    const payload = {
      postId,
      evaluationId,
      reason,
      isRelevant: isRelevant || isRelevant === null,
      compliance: isRelevant === false ? '' : compliance,
      adType: isRelevant === false ? '' : adType
    };
    return isEditing ? updateCompliance(payload) : createCompliance(payload);
  };

  const handleAdTypeChange = (button: Posts.adType) => {
    if (button === 'organic') {
      setCompliance('');
    }
    setAdType(button);
  };

  const handleDeleteRating = () => {
    deleteCompliance({ postId, evaluationId });
  };

  const handleComplianceChange = (event: SelectChangeEvent) => {
    setCompliance(event.target.value as Posts.ComplianceType);
  };

  const handleCancel = () => {
    if (data) {
      fillStates(data);
      setEditing(false);
    }
  };

  const complianceOptions = [
    { value: 'compliant', label: 'Compliant', color: 'success' },
    { value: 'questionable', label: 'Questionable', color: 'warning' },
    { value: 'violation', label: 'Violation', color: 'error' }
  ];

  const ComplianceAlert = useMemo(
    () => alertReturn(compliance || '', isRelevant, adType),
    [compliance, isRelevant, adType]
  );

  if (!data) return <Typography variant="body2">Data not available</Typography>;

  const { isExpired } = data;

  const isRatingEmpty =
    isRelevant !== false &&
    adType !== 'organic' &&
    (compliance === 'unknown' || compliance === '');

  const hasRatingChanged =
    data.evaluation.compliance.compliance !== compliance ||
    data.evaluation.compliance.reason !== reason ||
    data.evaluation.compliance.isRelevant !== isRelevant ||
    data.evaluation.compliance.adType !== adType;

  return (
    <>
      <Card
        title="Rating"
        content={
          <Stack gap={2}>
            {!isRated || isEditing ? (
              <>
                <Checkbox
                  value={isRelevant}
                  checked={isRelevant === false}
                  onChange={(event) => setIsRelevant(!event.target.checked)}
                  disabled={isExpired}
                  label={<Typography variant="body2">Not relevant</Typography>}
                />

                {(isRelevant || isRelevant === null) && (
                  <>
                    <Divider />
                    <Typography variant="body2">
                      What type of content is displayed in the post?
                    </Typography>
                    <ButtonGroup
                      id="evaluation-rating"
                      fullWidth
                      variant="outlined"
                      sx={{
                        flexWrap: 'wrap', // Allow buttons to wrap
                        '& .MuiButton-root': {
                          flex: '1 1 calc(33.333% - 8px)', // Make buttons responsive
                          textTransform: 'none',
                          fontSize: { xs: '0.7rem', sm: '0.8rem' }, // Adjust font size for small screens
                          minWidth: 100 // Ensure a minimum button width
                        }
                      }}
                      disabled={isExpired}
                    >
                      {['advertisement', 'questionable', 'organic'].map(
                        (type) => (
                          <MuiButton
                            key={type}
                            onClick={() =>
                              handleAdTypeChange(type as Posts.adType)
                            }
                            variant={adType === type ? 'contained' : 'outlined'}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MuiButton>
                        )
                      )}
                    </ButtonGroup>
                    {['advertisement', 'questionable'].includes(adType) && (
                      <>
                        <Typography variant="body2">
                          Does this content comply with the advertising rules?
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={compliance}
                            IconComponent={KeyboardArrowDown}
                            onChange={handleComplianceChange}
                            color={selectColor[compliance]}
                          >
                            {complianceOptions.map((option) => (
                              <MenuItem
                                id="evaluation-rating-type"
                                key={option.value}
                                value={option.value}
                              >
                                <Stack
                                  gap={1}
                                  direction="row"
                                  alignItems="center"
                                >
                                  <Circle
                                    color={
                                      option.color as
                                        | 'success'
                                        | 'warning'
                                        | 'error'
                                    }
                                    fontSize="inherit"
                                  />
                                  <Typography>{option.label}</Typography>
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </>
                )}
                <Stack gap="4px">
                  <Typography variant="subtitle2">Why?</Typography>
                  <TextField
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    label="Optional"
                    disabled={isExpired}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (
                          reason.trim() &&
                          (!isRatingEmpty || !hasRatingChanged)
                        )
                          handleClickSave();
                      }
                    }}
                  />
                </Stack>
              </>
            ) : (
              <>
                <Alert
                  variant="outlined"
                  sx={{
                    color: 'text.primary',
                    border: '2px solid #dadada'
                  }}
                  title={ComplianceAlert.title}
                  icon={ComplianceAlert.icon}
                  content={
                    <>
                      <Typography>{ComplianceAlert.content}</Typography>
                      <Typography my={1}>
                        {`${author?.name}, ${formatDate(
                          author?.date,
                          'dd/MM/yyyy'
                        )}`}
                      </Typography>
                      {reason && (
                        <>
                          <Divider />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: expanded ? 'none' : 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor:
                                reason.split(' ').length > 17
                                  ? 'pointer'
                                  : 'default'
                            }}
                            mt={1}
                            onClick={() => setExpanded(!expanded)}
                          >
                            {reason}
                          </Typography>
                        </>
                      )}
                    </>
                  }
                />
              </>
            )}
          </Stack>
        }
        actions={
          !isRated || isEditing ? (
            <>
              {isEditing && (
                <Button
                  variant="text"
                  text="cancel"
                  disabled={isUpdating || isCreating}
                  onClick={handleCancel}
                />
              )}
              <Button
                id="evaluation-rating-save"
                disabled={isRatingEmpty || !hasRatingChanged}
                text={!isRated ? 'rate post' : 'save rating'}
                loading={isUpdating || isCreating}
                onClick={handleClickSave}
              />
            </>
          ) : (
            <>
              <Button
                variant="text"
                text="remove"
                onClick={() => setDialogOpen(true)}
                disabled={isExpired}
              />
              <Button
                text="edit"
                onClick={() => setEditing(true)}
                disabled={isExpired}
              />
            </>
          )
        }
      />
      <Dialog
        open={isDialogOpen}
        title="Remove rating"
        dialogContent={
          <Typography>
            Are you sure you want to remove the rating from this item?
          </Typography>
        }
        actions={
          <>
            <Button
              variant="text"
              text="cancel"
              disabled={isDeleting}
              onClick={() => setDialogOpen(false)}
            />
            <Button
              color="error"
              loading={isDeleting}
              text="delete"
              onClick={handleDeleteRating}
            />
          </>
        }
      />
    </>
  );
};

export default PostCompliance;
